import { useState } from "react";
import { placeOrder } from "./api";
import { useCart } from "./cart";
import { categoryIcon } from "./categoryIcon";
import { useCustomer } from "./customer";
import { formatMoney } from "./money";
import type { OrderSummary } from "./OrderConfirmation";
import { withSpan } from "./otel";
import { signIn } from "./session";

type Phase = { name: "cart" } | { name: "placing" } | { name: "error"; message: string };

export function CartDrawer({
  open,
  onClose,
  onPlaced,
}: {
  open: boolean;
  onClose: () => void;
  // Checkout succeeded — App swaps the product grid for the confirmation
  // view (#187), so a live-tracked order gets a whole screen, not a panel.
  onPlaced: (summary: OrderSummary) => void;
}) {
  const { lines, subtotal, setQuantity, remove, clear } = useCart();
  const { selected } = useCustomer();
  const [phase, setPhase] = useState<Phase>({ name: "cart" });

  const canCheckout = phase.name === "cart" && lines.length > 0;

  async function checkout() {
    setPhase({ name: "placing" });
    const items = lines.map((l) => ({
      sku: l.product.sku,
      quantity: l.quantity,
      unitPrice: l.product.unitPrice,
    }));
    const itemCount = lines.reduce((n, l) => n + l.quantity, 0);
    const total = subtotal;

    const result = await withSpan(
      "storefront.checkout",
      { "customer.id": selected.customerId, "cart.item_count": items.length },
      async (span) => {
        const r = await placeOrder({ customerId: selected.customerId, items });
        if (r.kind === "placed") span.setAttribute("orderId", r.orderId);
        return r;
      },
    );

    if (result.kind === "placed") {
      const customerName = selected.name;
      clear();
      setPhase({ name: "cart" });
      onClose();
      onPlaced({ orderId: result.orderId, itemCount, total, customerName });
    } else if (result.kind === "unauthorized") {
      // The session expired mid-visit — send them back through login.
      signIn(window.location.pathname);
    } else {
      setPhase({ name: "error", message: result.message });
    }
  }

  function handleClose() {
    onClose();
    if (phase.name === "error") setPhase({ name: "cart" });
  }

  return (
    <>
      <div
        className={`drawer-overlay${open ? " is-open" : ""}`}
        onClick={handleClose}
        aria-hidden="true"
      />
      <aside
        className={`cart-drawer${open ? " is-open" : ""}`}
        aria-label="Cart"
        aria-hidden={!open}
      >
        <div className="cart-drawer-header">
          <h2>Your cart</h2>
          <button type="button" className="drawer-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        {lines.length === 0 ? (
          <p className="cart-empty">Your cart is empty.</p>
        ) : (
          <>
            <ul className="cart-lines">
              {lines.map(({ product, quantity }) => (
                <li key={product.sku} className="cart-line">
                  <span className="cart-line-icon" aria-hidden="true">
                    {categoryIcon(product.category)}
                  </span>
                  <div className="cart-line-body">
                    <div className="cart-line-top">
                      <span className="cart-line-name">{product.name}</span>
                      <span className="cart-line-total">
                        {formatMoney(product.unitPrice * quantity)}
                      </span>
                    </div>
                    <span className="cart-line-unit">{formatMoney(product.unitPrice)} each</span>
                    <div className="qty-stepper">
                      <button
                        type="button"
                        onClick={() => setQuantity(product.sku, quantity - 1)}
                        aria-label={`Decrease ${product.name} quantity`}
                        disabled={phase.name === "placing"}
                      >
                        −
                      </button>
                      <span className="qty-value" aria-label={`Quantity ${quantity}`}>
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuantity(product.sku, quantity + 1)}
                        aria-label={`Increase ${product.name} quantity`}
                        disabled={phase.name === "placing"}
                      >
                        +
                      </button>
                      <button
                        type="button"
                        className="line-remove"
                        onClick={() => remove(product.sku)}
                        disabled={phase.name === "placing"}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="cart-footer">
              <div className="cart-subtotal">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal)}</span>
              </div>

              {phase.name === "error" && (
                <p className="checkout-error">Couldn't place the order: {phase.message}</p>
              )}

              <p className="checkout-as">
                Placing as {selected.name} · {selected.tier}
              </p>

              <button
                type="button"
                className={`checkout-button${canCheckout ? " is-enabled" : ""}`}
                disabled={!canCheckout}
                onClick={checkout}
              >
                {phase.name === "placing"
                  ? "Placing order…"
                  : phase.name === "error"
                    ? "Try again"
                    : "Place order"}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
